import { useState } from 'react'

import { useColaDeFotos } from '@/modules/campo/useColaDeFotos'
import { FotosOrden } from '@/modules/ordenes-servicio/FotosOrden'
import { useFotosDeLaOrden } from '@/modules/ordenes-servicio/useFotosOrden'
import { Modal } from '@/shared/ui/Modal/Modal'

import { useRegistrarVisita } from './useVisitas'
import estilos from './RegistrarVisita.module.css'

/**
 * LA VISITA DE CERTIFICACIÓN, EN LA OBRA (0256).
 *
 * Desde la operación: «que el técnico tome registro fotográfico, observación,
 * y si el usuario en esa visita dice que le coticemos nosotros, ya que no
 * tiene personal eléctrico para ejecutarla, que haya una opción de desea
 * cotizar sí o no». Tres cosas, en el orden en que pasan:
 *   1. Las fotos de cómo está la instalación (las mismas «fotos de la
 *      visita» de la orden; sin señal esperan en el teléfono).
 *   2. Qué encontró.
 *   3. Si el cliente quiere que le coticemos la ejecución. Si dice que sí,
 *      al registrar se abre el cotizador ahí mismo.
 */
export function RegistrarVisita({
  ordenServicioId,
  numero,
  direccion,
  onCerrar,
  onCotizar,
}: {
  ordenServicioId: string
  numero: string
  direccion: string | null
  onCerrar: () => void
  /** El cliente quiere cotización: se abre el cotizador. */
  onCotizar: () => void
}) {
  const registrar = useRegistrarVisita()
  const { data: subidas = [] } = useFotosDeLaOrden(ordenServicioId)
  const { enCola } = useColaDeFotos(ordenServicioId)
  const [observacion, setObservacion] = useState('')
  const [quiere, setQuiere] = useState<boolean | null>(null)

  const fotos = subidas.length + enCola.length
  const faltaQueSuban = subidas.length === 0 && enCola.length > 0
  const sinRed = typeof navigator !== 'undefined' && navigator.onLine === false
  const listo = fotos > 0 && observacion.trim().length >= 5 && quiere !== null

  async function guardar() {
    if (quiere === null) return
    await registrar.mutateAsync({
      ordenServicioId,
      observacion: observacion.trim(),
      quiereCotizar: quiere,
    })
    if (quiere) onCotizar()
    else onCerrar()
  }

  return (
    <Modal
      abierto
      titulo={`Visita de certificación · ${numero}`}
      subtitulo={direccion ?? undefined}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className={estilos.guardar}
          onClick={() => void guardar()}
          disabled={!listo || registrar.isPending}
        >
          {registrar.isPending
            ? 'Guardando…'
            : quiere
            ? 'Registrar y cotizar'
            : 'Registrar la visita'}
        </button>
      }
    >
      <div className={estilos.pasos}>
        <section className={estilos.paso}>
          <p className={estilos.pasoTitulo}>
            <span className={fotos > 0 ? estilos.numeroHecho : estilos.numero}>1</span>
            Fotos de la instalación
          </p>
          <FotosOrden ordenServicioId={ordenServicioId} sello={{ referencia: numero, direccion }} />
        </section>

        <section className={estilos.paso}>
          <p className={estilos.pasoTitulo}>
            <span className={observacion.trim().length >= 5 ? estilos.numeroHecho : estilos.numero}>
              2
            </span>
            Observación
          </p>
          <textarea
            className={estilos.observacion}
            rows={4}
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            placeholder="Qué encontró: estado del tablero, puesta a tierra, acometida, lo que falta para certificar…"
          />
        </section>

        <section className={estilos.paso}>
          <p className={estilos.pasoTitulo}>
            <span className={quiere !== null ? estilos.numeroHecho : estilos.numero}>3</span>
            ¿El cliente quiere que le coticemos la ejecución?
          </p>
          <p className={estilos.pasoAyuda}>
            Si no tiene personal eléctrico para hacer lo que falta, MEGA se lo puede cotizar.
          </p>
          <div className={estilos.siNo} role="group" aria-label="¿Quiere que le coticemos?">
            <button
              type="button"
              className={quiere === true ? estilos.siVivo : undefined}
              aria-pressed={quiere === true}
              onClick={() => setQuiere(true)}
            >
              Sí, cotizar
            </button>
            <button
              type="button"
              className={quiere === false ? estilos.noVivo : undefined}
              aria-pressed={quiere === false}
              onClick={() => setQuiere(false)}
            >
              No
            </button>
          </div>
        </section>

        {(faltaQueSuban || sinRed) && (
          <p className={estilos.aviso}>
            Sin señal no pasa nada: la visita queda guardada en el teléfono y se envía sola, con sus
            fotos, cuando vuelva la cobertura.
          </p>
        )}
        {registrar.error && <p className={estilos.error}>{registrar.error.message}</p>}
      </div>
    </Modal>
  )
}
